import React, { useEffect, useCallback } from 'react';
import jwtDecode from 'jwt-decode';
import DividerWithText from "Common/components/DividerLineWithText";

function AppleSignIn({ onSuccess, onError }) {
    // 处理Apple登录成功
    const handleAppleCallback = useCallback(
        async (response) => {
            try {
                // 检查响应是否存在
                if (!response) {
                    throw new Error('No response from Apple');
                }

                sessionStorage.setItem('response', JSON.stringify(response));

                // 检查Apple返回的响应结构
                if (!response.authorization || !response.authorization.id_token) {
                    throw new Error('Missing authorization.id_token from Apple response');
                }

                // 检查token类型
                if (typeof response.authorization.id_token !== 'string') {
                    throw new Error('id_token must be a string');
                }

                // 解码Apple返回的JWT token
                const userObject = jwtDecode(response.authorization.id_token);
                sessionStorage.setItem('userObject', JSON.stringify(userObject));

                // 调用后端API进行Apple登录验证
                const result = await fetch('/api/v1/los/auth/appleSignin', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-APPLETOKEN': response.authorization.id_token,
                    },
                    body: JSON.stringify({
                        accessToken: response.authorization.id_token,
                        user: userObject,
                        authorizationCode: response.authorization.code,
                        identityToken: response.authorization.id_token,
                        // Apple官方标准字段
                        userIdentifier: userObject.sub, // 从JWT中获取用户ID
                        fullName: response.fullName || null, // 可能不存在
                        email: userObject.email || response.email || null, // 优先使用JWT中的邮箱
                        realUserStatus: response.realUserStatus || null, // 可能不存在
                    }),
                });

                if (result.status !== 200) {
                    throw new Error('Apple login failed');
                }

                const data = await result.json();
                if (data.code !== 0) {
                    throw new Error(data.errMessage || 'Apple login failed');
                }

                // 处理返回的数据数组
                const companyDataList = data.data || [];
                
                if (companyDataList.length === 0) {
                    // 如果数据为空数组，表示账户未找到，应该显示账户未找到的提示
                    const error = new Error('Account not found');
                    error.isAccountNotFound = true;
                    throw error;
                }

                // 存储用户邮箱信息
                const userEmail = userObject.email?.toLowerCase().trim() || '';
                sessionStorage.setItem('userEmail', userEmail);

                if (userObject.name) {
                    const nameParts = userObject.name.split(' ');
                    sessionStorage.setItem('userFirstName', nameParts[0]);
                    if (nameParts.length > 1) {
                        sessionStorage.setItem(
                            'userLastName',
                            nameParts.slice(1).join(' ')
                        );
                    }
                }

                if (onSuccess) {
                    onSuccess({
                        companyDataList,
                        email: userEmail
                    });
                }
            } catch (error) {
                console.error('Apple login failed:', error);
                if (onError) {
                    onError(error);
                }
            }
        },
        [onSuccess, onError]
    );

    // 初始化Apple登录
    useEffect(() => {
        const initAppleSignIn = () => {
            if (window.AppleID) {
                try {
                    window.AppleID.auth.init({
                        clientId: 'com.zeitro.signinservice', // Apple Service ID
                        scope: 'name email',
                        redirectURI: window.location.origin + '/login', // 使用当前域名
                        state: 'apple_signin',
                        usePopup: true,
                        nonce: 'apple_signin',
                        responseMode: 'query',
                    });
                } catch (error) {
                    console.error('Apple Sign In initialization failed:', error);
                }
            }
        };

        // 添加Apple登录脚本
        const script = document.createElement('script');
        script.src =
            'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        script.async = true;
        script.onload = initAppleSignIn;
        document.head.appendChild(script);

        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, []);

    // 处理Apple登录按钮点击
    const handleAppleSignIn = async () => {
        try {
            if (window.AppleID?.auth?.signIn) {
                const response = await window.AppleID.auth.signIn();
                await handleAppleCallback(response);
            } else {
                throw new Error('Apple Sign In not available');
            }
        } catch (error) {
            console.error('Apple Sign In failed:', error);
            if (onError) {
                onError(error);
            }
        }
    };

    return (
        <div className="apple-signin-container" style={{
            width: '100%',
            maxWidth: window.innerWidth <= 768 ? '320px' : '400px',
            margin: '0 auto'
        }}>
            <button
                type="button"
                onClick={handleAppleSignIn}
                className="apple-signin-button"
                style={{
                    width: '100%',
                    height: '40px',
                    backgroundColor: '#fff',
                    border: '1px solid #dadce0',
                    color: '#222',
                    borderRadius: '24px',
                    fontSize: '18px',
                    lineHeight: '22px',
                    fontFamily: 'Inter',
                    fontWeight: '400',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                }}
                onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#f7f8ff';
                    e.currentTarget.style.border = '1px solid #d8eaff';
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                    e.currentTarget.style.border = '1px solid #dadce0';
                }}
            >
                <svg width="22" height="22" viewBox="0 0 22 22" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.11-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <span style={{ width: '177px', textAlign: 'left'}}>Sign in with Apple</span>
            </button>
            {/* <DividerWithText text="OR" /> */}
        </div>
    );
}

export default AppleSignIn;
